import React, { useState, useEffect, useMemo, useRef } from 'react';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';

// Helper function to convert a string to uppercase
const toUpperCase = (value) => {
  if (typeof value !== 'string') return value;
  return value.toUpperCase();
};

// Status options
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'ACTIVE' },
  { value: 'INACTIVE', label: 'INACTIVE' },
];
// Inventory flag options
const INVENTORY_FLAG_OPTIONS = [
  { value: 'yes', label: 'YES' },
  { value: 'no', label: 'NO' },
];

const initialHeader = {
  receiptId: '',
  receiptDate: new Date().toISOString().split('T')[0],
  productionOrderId: '',
  productionOrderQty: '',
  branchCode: '',
  receivedByUserId: '',
  receivedByUserName: '',
  inventoryFlag: 'yes',
  status: 'ACTIVE',
  department_id: "",
};

const initialItem = {
  receiptDetailID: '',
  productionOrderDetailID: '',
  materialType: '',
  productCode: '',
  productName: '',
  qty: '',
  calculatedQty: '',
  rate: '',
  value: '0',
  flushType: '',
  uom: '',
  batchNumber: "",
  expiryDate: "",
  warehouse: '',
  allowBatch: false,
  department_id: "",
  // Add tracking fields
  productTrackingType: 0, // 0: None, 1: Batch, 2: Serial
  batches: [], // For batch tracking
  serialNumbers: [], // For serial tracking
  showBatchModal: false,
  showSerialModal: false,
};

// BatchTableModal Component
const BatchTableModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialBatches = [], 
  productName = "", 
  totalQuantity = 0,
  acceptedQuantity = 0 
}) => {
  const [batches, setBatches] = useState([
    { batchNumber: "", expiryDate: "", quantity: "" }
  ]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      console.log('BatchTableModal opened with initialBatches:', initialBatches);
      console.log('BatchTableModal productName:', productName);
      console.log('BatchTableModal acceptedQuantity:', acceptedQuantity);
      
      if (initialBatches.length > 0) {
        // Format the expiry dates to YYYY-MM-DD format for the input[type="date"]
        const formattedBatches = initialBatches.map(batch => ({
          ...batch,
          expiryDate: batch.expiryDate ? new Date(batch.expiryDate).toISOString().split('T')[0] : ""
        }));
        console.log('Formatted batches:', formattedBatches);
        setBatches(formattedBatches);
      } else {
        console.log('No initial batches, setting empty batch');
        setBatches([{ batchNumber: "", expiryDate: "", quantity: "" }]);
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, initialBatches, productName, acceptedQuantity]);

  // Calculate remaining quantity
  const remainingQuantity = acceptedQuantity - batches.reduce(
    (sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0
  );

  // Handle batch field changes
  const handleBatchChange = (index, field, value) => {
    const newBatches = [...batches];
    newBatches[index][field] = value;
    
    // Clear errors for this field
    if (errors[`${field}_${index}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${field}_${index}`];
      setErrors(newErrors);
    }
    
    // Auto-generate new row if quantity is entered and it's less than remaining
    if (field === 'quantity' && value && index === batches.length - 1) {
      const enteredQty = parseFloat(value) || 0;
      const currentTotal = batches.reduce((sum, batch, i) => 
        i === index ? sum + enteredQty : sum + (parseFloat(batch.quantity) || 0), 0);
      
      // If there's still remaining quantity after this entry, add a new row
      if (currentTotal < acceptedQuantity) {
        // Add a new empty row
        newBatches.push({ batchNumber: "", expiryDate: "", quantity: "" });
      }
    }
    
    setBatches(newBatches);
  };

  // Remove a batch row
  const removeBatchRow = (index) => {
    if (batches.length > 1) {
      const newBatches = [...batches];
      newBatches.splice(index, 1);
      setBatches(newBatches);
    }
  };

  // Auto-fill quantity to cover remaining
  const autoFillQuantity = (index) => {
    const newBatches = [...batches];
    newBatches[index].quantity = remainingQuantity.toString();
    
    // Remove any extra empty rows if we've covered the full quantity
    if (remainingQuantity > 0) {
      // Keep only this row if it covers the full remaining quantity
      setBatches([newBatches[index]]);
    } else {
      setBatches(newBatches);
    }
  };

  // Validate all batches
  const validateBatches = () => {
    const newErrors = {};
    let isValid = true;

    // Check if we have at least one batch
    if (batches.length === 0) {
      newErrors.general = "At least one batch is required";
      isValid = false;
    }

    batches.forEach((batch, index) => {
      // Validate batch number
      if (!batch.batchNumber || batch.batchNumber.trim() === '') {
        newErrors[`batchNumber_${index}`] = "Batch number is required";
        isValid = false;
      }

      // Validate expiry date
      if (!batch.expiryDate) {
        newErrors[`expiryDate_${index}`] = "Expiry date is required";
        isValid = false;
      }

      // Validate quantity
      if (!batch.quantity || batch.quantity.trim() === '' || parseFloat(batch.quantity) <= 0) {
        newErrors[`quantity_${index}`] = "Quantity must be greater than 0";
        isValid = false;
      }
    });

    // Check if total batch quantity equals accepted quantity
    const totalBatchQty = batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0
    );
    
    if (Math.abs(totalBatchQty - acceptedQuantity) > 0.001) {
      newErrors.total = `Total batch quantity (${totalBatchQty.toFixed(2)}) must equal accepted quantity (${acceptedQuantity})`;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    
    try {
      if (validateBatches()) {
        // Filter out any incomplete batches (only include batches with all required fields)
        const validBatches = batches.filter(batch => 
          batch.batchNumber && 
          batch.batchNumber.trim() !== '' && 
          batch.expiryDate && 
          batch.quantity && 
          parseFloat(batch.quantity) > 0
        );
        
        // Format batches for backend
        const formattedBatches = validBatches.map(batch => ({
          batchNumber: batch.batchNumber.trim(),
          expiryDate: batch.expiryDate,
          quantity: batch.quantity.toString()
        }));
        
        await onSave(formattedBatches);
        onClose();
      }
    } catch (error) {
      console.error('Error saving batches:', error);
      setErrors({
        ...errors,
        general: error.message || "Failed to save batches"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-0 max-h-[90vh] flex flex-col border border-gray-300">
        {/* Header */}
        <div className="bg-slate-200 px-6 py-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-black">Batch Details</h3>
            <button 
              onClick={onClose} 
              className="text-gray-900 hover:text-black transition-colors"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-slate-100 px-6 py-3 border-b border-slate-200">
          <div className="flex items-center space-x-8">
            <div className="flex items-center">
              <span className="text-sm font-medium text-slate-600 mr-2">Product:</span>
              <span className="text-sm font-semibold text-slate-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm font-medium text-slate-600 mr-2">Total Quantity:</span>
              <span className="text-sm font-semibold text-slate-800">{acceptedQuantity}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm font-medium text-slate-600 mr-2">Remaining:</span>
              <span className={`text-sm font-semibold ${remainingQuantity > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {remainingQuantity.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
          <p className="text-sm text-blue-700">
            <span className="font-medium">Instructions:</span> Enter batch details for each batch. 
            New rows will be automatically added when you enter a quantity less than the total required. 
            Use the "Fill" button to automatically fill a row with the remaining quantity.
          </p>
        </div>
        
        {/* Error Messages */}
        {(errors.general || errors.total) && (
          <div className="mx-6 mt-4 space-y-2">
            {errors.general && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{errors.general}</p>
              </div>
            )}
            {errors.total && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{errors.total}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Batch Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-6">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100 sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-12">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Batch Number *</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Expiry Date *</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-32">Quantity *</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {batches.map((batch, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50 hover:bg-slate-100'}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                     <input
  type="text"
  value={batch.batchNumber}
  onChange={(e) => handleBatchChange(index, 'batchNumber', e.target.value.toUpperCase())}
  className={`w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 uppercase text-sm ${errors[`batchNumber_${index}`] ? 'border-red-500' : ''}`}
  placeholder="Enter batch number"
/>
                      {errors[`batchNumber_${index}`] && (
                        <p className="text-red-500 text-xs mt-1">{errors[`batchNumber_${index}`]}</p>
                      )}
                    </td>
                     <td className="px-4 py-3 whitespace-nowrap">
    <input
      type="date"
      value={batch.expiryDate}
      onChange={(e) => handleBatchChange(index, 'expiryDate', e.target.value)}
      className={`w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm ${errors[`expiryDate_${index}`] ? 'border-red-500' : ''}`}
    />
    {errors[`expiryDate_${index}`] && (
      <p className="text-red-500 text-xs mt-1">{errors[`expiryDate_${index}`]}</p>
    )}
  </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={batch.quantity}
                          onChange={(e) => handleBatchChange(index, 'quantity', e.target.value)}
                          className={`w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm ${errors[`quantity_${index}`] ? 'border-red-500' : ''}`}
                          placeholder="0.00"
                        />
                        <button
                          type="button"
                          onClick={() => autoFillQuantity(index)}
                          className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs hover:bg-blue-200"
                          title="Auto-fill with remaining quantity"
                        >
                          Fill
                        </button>
                      </div>
                      {errors[`quantity_${index}`] && (
                        <p className="text-red-500 text-xs mt-1">{errors[`quantity_${index}`]}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex space-x-1">
                        <button
                          type="button"
                          onClick={() => removeBatchRow(index)}
                          disabled={batches.length <= 1}
                          className={`p-1 rounded-md ${batches.length <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-100'}`}
                          title="Remove batch"
                        >
                          <TrashIcon size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="text-sm text-slate-600">
              Total batches: <span className="font-medium">{batches.length}</span> | 
              Remaining quantity: <span className={`font-medium ${remainingQuantity > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {remainingQuantity.toFixed(2)}
              </span>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors disabled:opacity-50"
                onClick={handleSave}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save Batch Details'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// SerialModal Component
const SerialModal =  ({ isOpen, onClose, onSave, count = 0, initialSerialNumbers = [], productName = "", locationCode = "" }) => {
  const [serialNumbers, setSerialNumbers] = useState(
    Array.from({ length: count }, (_, i) => initialSerialNumbers[i] || "")
  );
  const inputRefs = useRef([]);

 useEffect(() => {
  if (isOpen) {
    console.log('SerialModal opened with initialSerialNumbers:', initialSerialNumbers);
    console.log('SerialModal count:', count);
    console.log('SerialModal productName:', productName);
    console.log('SerialModal locationCode:', locationCode);
    
    if (initialSerialNumbers.length > 0) {
      console.log('Setting initial serial numbers:', initialSerialNumbers);
      setSerialNumbers(initialSerialNumbers);
    } else {
      console.log('No initial serial numbers, setting empty array');
      setSerialNumbers(Array.from({ length: count }, () => ""));
    }
    
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }
}, [isOpen, count, initialSerialNumbers, productName, locationCode]);

  const handleSerialChange = (index, value) => {
    const newSerialNumbers = [...serialNumbers];
    newSerialNumbers[index] = value;
    setSerialNumbers(newSerialNumbers);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (index < count - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleSave = () => {
    if (serialNumbers.every(sn => sn.trim() !== "")) {
      onSave(serialNumbers.map(sn => sn.trim()));
      onClose();
    } else {
      alert("All serial numbers are required");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl p-0 max-h-[90vh] flex flex-col border border-gray-300">
        {/* Header */}
        <div className="bg-slate-200 px-4 py-2 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-black">Serial Number Entry</h3>
            <button 
              onClick={onClose} 
              className="text-gray-900 hover:text-black transition-colors"
            >
              <CloseIcon size={20} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
          <div className="flex items-center space-x-6">
            <div className="flex items-center">
              <span className="text-xs font-medium text-slate-600 mr-1">Product:</span>
              <span className="text-xs font-semibold text-slate-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-slate-600 mr-1">Location:</span>
              <span className="text-xs font-semibold text-slate-800">{locationCode || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-slate-600 mr-1">Quantity:</span>
              <span className="text-xs font-semibold text-slate-800">{count}</span>
            </div>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
          <p className="text-xs text-slate-700">
            Enter {count} unique serial number{count !== 1 ? 's' : ''}. Press Enter or Tab to move to the next field.
          </p>
        </div>
        
        {/* Serial Numbers Table */}
        <div className="flex-grow overflow-hidden flex flex-col">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-10">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Serial Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Location</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-10">Qty</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {Array.from({ length: count }).map((_, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50 hover:bg-slate-100'}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-slate-900">
                      {index + 1}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <input
                        ref={el => inputRefs.current[index] = el}
                        type="text"
                        value={serialNumbers[index]}
                        onChange={(e) => handleSerialChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        className="w-full border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 uppercase text-xs"
                        placeholder={`Serial ${index + 1}`}
                      />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-900">
                      {productName || "N/A"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">
                      {locationCode || "N/A"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600 text-center">
                      1
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-slate-50 px-4 py-2 border-t border-slate-200 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="text-xs text-slate-600">
              {serialNumbers.filter(sn => sn.trim() !== "").length} of {count} serial numbers entered
            </div>
            <div className="flex space-x-2">
              <button
                className="px-3 py-1 border border-slate-300 rounded text-xs text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-500 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 border border-transparent rounded text-xs text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-500 transition-colors"
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// SearchableDropdown Component
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const secondaryLabel = getOptionSecondaryLabel(option).toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    if (!option) {
      console.warn('Selected option is undefined');
      return;
    }
    
    const optionValue = getOptionValue(option);
    if (optionValue === undefined || optionValue === null) {
      console.warn('Option value is undefined or null');
      return;
    }
    
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// Main ReceiptFormModal Component
const ReceiptFormModal = ({ open, onClose, onSubmit, initialData, isEdit, currentUser }) => {
  const [header, setHeader] = useState(initialHeader);
  const [items, setItems] = useState([{ ...initialItem }]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    productionOrders: [],
    branches: [],
    warehouses: [],
    products: [],
    nextReceiptNumber: ''
  });
  
  // Add state for original quantities
  const [originalProductionOrderQty, setOriginalProductionOrderQty] = useState(0);
  const [originalByproductQtys, setOriginalByproductQtys] = useState([]);
  
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);
  
  // Tracking modals state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [currentBatchItem, setCurrentBatchItem] = useState(null);
  const [currentSerialItem, setCurrentSerialItem] = useState(null);
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`);
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`);
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the ReceiptFromProduction screen configuration
      const receiptScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'ReceiptFromProduction'.toLowerCase()
      );
      
      console.log('ReceiptFromProduction screen configuration:', receiptScreen);
      if (receiptScreen) {
        setScreenConfig(receiptScreen);
        // Show department field if ShowOnHeader is true
        const shouldShowDepartment = receiptScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // Show department item field if ShowOnItem is true
        const shouldShowDepartmentItem = receiptScreen.ShowOnItem === true;
        setShowDepartmentItemField(shouldShowDepartmentItem);
        
        // If department fields should be shown, fetch departments
        if (shouldShowDepartment || shouldShowDepartmentItem) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
      setShowDepartmentItemField(false);
    }
  };
  
  // Toggle maximize/minimize function
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  // Calculate item value
  const calculateItemValue = (item) => {
    const quantity = parseFloat(item.qty) || 0;
    const calculatedQty = parseFloat(item.calculatedQty) || 0;
    const rate = parseFloat(item.rate) || 0;
    const value = calculatedQty * rate;
    return value.toFixed(2);
  };
  
  // Calculate summary values
  const summary = useMemo(() => {
    let totalQuantity = 0;
    let totalValue = 0;
    
    items.forEach(item => {
      const quantity = parseFloat(item.qty) || 0;
      const value = parseFloat(item.value) || 0;
      totalQuantity += quantity;
      totalValue += value;
    });
    
    return {
      totalQuantity: totalQuantity.toString(),
      totalValue: totalValue.toFixed(2)
    };
  }, [items]);
  
  // Function to recalculate byproduct quantities when header qty changes
  const recalculateByproducts = (headerQty) => {
    if (!headerQty || !originalProductionOrderQty || originalProductionOrderQty === 0) {
      return;
    }

    setItems(prevItems => {
      const newItems = [...prevItems];
      
      // Only update byproducts (starting from index 1)
      for (let i = 1; i < newItems.length; i++) {
        const originalByproductQty = originalByproductQtys[i - 1]; // Index adjustment for byproducts
        if (originalByproductQty !== undefined) {
          const calculatedQty = (parseFloat(headerQty) / originalProductionOrderQty) * originalByproductQty;
          newItems[i].calculatedQty = calculatedQty.toFixed(2);
          newItems[i].qty = calculatedQty.toFixed(2); // Set qty to calculated qty
          newItems[i].value = calculateItemValue(newItems[i]);
        }
      }
      
      return newItems;
    });
  };
  
  // Fetch next receipt number when creating a new record
// Fetch next receipt number when creating a new record
useEffect(() => {
  if (open && !isEdit) {
    const fetchNextReceiptNumber = async () => {
      try {
        const response = await fetch(`${API_URL}/receipt-from-production/next-number`);
        if (response.ok) {
          const data = await response.json();
          setHeader(prev => ({
            ...prev,
            receiptId: data.nextReceiptNumber || ''
          }));
        }
      } catch (error) {
        console.error('Error fetching next receipt number:', error);
      }
    };
    
    fetchNextReceiptNumber();
  }
}, [open, isEdit]);
  
  useEffect(() => {
    if (open) {
      setIsMaximized(false);
      fetchScreenConfig();
      
      const initializeForm = async () => {
        try {
          // Check if we have a valid current user
          if (!currentUser || !currentUser.UserID) {
            alert('User information not available. Please refresh the page and log in again.');
            onClose();
            return;
          }
          
          fetchDropdownData();
          
          if (isEdit && initialData) {
            setHeader({
              receiptId: initialData.ReceiptID || '',
              receiptDate: initialData.ReceiptDate ? new Date(initialData.ReceiptDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              productionOrderId: initialData.ProductionOrderID || '',
              productionOrderQty: initialData.ProductionOrderQty?.toString() || '',
              branchCode: initialData.BranchCode || '',
              receivedByUserId: initialData.ReceivedByUserID?.toString() || '',
              receivedByUserName: initialData.ReceivedByUserName || '',
              inventoryFlag: initialData.InventoryFlag ? 'yes' : 'no',
              status: initialData.Status || 'ACTIVE',
              department_id: initialData.DepartmentID?.toString() || "",
            });
            
            if (initialData.Details && initialData.Details.length > 0) {
              setItems(initialData.Details.map(item => ({
                receiptDetailID: item.ReceiptDetailID || '',
                productionOrderDetailID: item.ProductionOrderDetailID || '',
                materialType: item.MaterialType || '',
                productCode: item.ProductCode || '',
                productName: item.ProductName || '',
                qty: item.Qty?.toString() || '',
                calculatedQty: item.CalculatedQty?.toString() || '',
                rate: item.Rate?.toString() || '',
                value: item.Value?.toString() || '0',
                flushType: item.FlushType || '',
                uom: item.UOM || '',
                batchNumber: item.BatchNumber || "",
                expiryDate: item.ExpiryDate ? new Date(item.ExpiryDate).toISOString().split('T')[0] : "",
                warehouse: item.Warehouse || '',
                allowBatch: !!(item.BatchNumber && item.BatchNumber.trim() !== ""),
                department_id: item.DepartmentID?.toString() || "",
                // Add tracking fields
                productTrackingType: parseInt(item.ProductTrackingType) || 0,
                batches: (item.Batches || []).map(batch => ({
  batchNumber: batch.BatchNumber || batch.batchNumber || "",
  expiryDate: batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] : "",
  quantity: batch.Quantity || batch.quantity || 0
})),
                serialNumbers: item.SerialNumbers || [],
              })));
            } else {
              setItems([{ ...initialItem }]);
            }
            
            // Fetch production order details for edit mode to get original quantities
            if (initialData.ProductionOrderID) {
              try {
                const response = await fetch(`${API_URL}/production-order/${initialData.ProductionOrderID}`);
                if (!response.ok) throw new Error('Failed to fetch production order details');
                const orderData = await response.json();
                
                // Get the production order quantity from the correct field
                const prodQty = parseFloat(orderData.Qty) || 0;
                setOriginalProductionOrderQty(prodQty);
                
                // Store original byproduct quantities
                const byproductQtys = orderData.Details.map(detail => parseFloat(detail.Qty) || 0);
                setOriginalByproductQtys(byproductQtys);
              } catch (error) {
                console.error('Error fetching production order details for edit mode:', error);
              }
            }
          } else {
            // For new records, set user info from currentUser prop
            setHeader({
              ...initialHeader,
              receivedByUserId: currentUser.UserID.toString(),
              receivedByUserName: currentUser.UserName || currentUser.FullName || currentUser.Email || ''
            });
            setItems([{ ...initialItem }]);
          }
          
          setErrors({});
          setIsSubmitting(false);
        } catch (error) {
          console.error('Error initializing form:', error);
          alert('Error initializing form. Please try again.');
          onClose();
        }
      };
      
      initializeForm();
    }
  }, [open, initialData, isEdit, onClose, currentUser]);

  const fetchNextDetailNumber = async () => {
  try {
    const response = await fetch(`${API_URL}/receipt-from-production/next-detail-number`);
    if (response.ok) {
      const data = await response.json();
      return data.nextDetailNumber || '';
    }
    return '';
  } catch (error) {
    console.error('Error fetching next detail number:', error);
    return '';
  }
};

const fetchNextTransactionId = async () => {
  try {
    const response = await fetch(`${API_URL}/transaction/next-id`);
    if (response.ok) {
      const data = await response.json();
      return data.nextTransactionId || 1;
    }
    return 1;
  } catch (error) {
    console.error('Error fetching next transaction ID:', error);
    return 1;
  }
};

const fetchCompanySettings = async () => {
  try {
    const response = await fetch(`${API_URL}/company/settings`);
    if (response.ok) {
      const data = await response.json();
      return data.allowDuplicateBatch || false;
    }
    return false;
  } catch (error) {
    console.error('Error fetching company settings:', error);
    return false;
  }
};
  
const fetchDropdownData = async () => {
  try {
    setLoading(true);
    
    // Fetch issued production orders
    console.log('Fetching issued production orders...');
    const ordersResponse = await fetch(`${API_URL}/issue-for-production`);
    if (!ordersResponse.ok) {
      const errorText = await ordersResponse.text();
      throw new Error(`Failed to fetch issued production orders: ${ordersResponse.status} - ${errorText}`);
    }
    const ordersData = await ordersResponse.json();
    console.log('Issued production orders fetched:', ordersData);
    
    // Fetch branches
    let branchesData = [];
    try {
      console.log('Fetching branches...');
      const branchesResponse = await fetch(`${API_URL}/branches`);
      if (branchesResponse.ok) {
        branchesData = await branchesResponse.json();
        console.log('Branches fetched:', branchesData);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
    
    // Fetch warehouses
    let warehousesData = [];
    try {
      console.log('Fetching warehouses...');
      const warehousesResponse = await fetch(`${API_URL}/locations`);
      if (warehousesResponse.ok) {
        warehousesData = await warehousesResponse.json();
        console.log('Warehouses fetched:', warehousesData);
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
    
    // Fetch products
    let productsData = [];
    try {
      console.log('Fetching products...');
      const productsResponse = await fetch(`${API_URL}/products`);
      if (productsResponse.ok) {
        productsData = await productsResponse.json();
        console.log('Products fetched:', productsData);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
    
    // Fetch users
    let usersData = [];
    try {
      console.log('Fetching users...');
      const usersResponse = await fetch(`${API_URL}/user`);
      if (usersResponse.ok) {
        usersData = await usersResponse.json();
        console.log('Users fetched:', usersData);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
    
    setDropdownData({
      productionOrders: ordersData || [],
      branches: branchesData || [],
      warehouses: warehousesData || [],
      products: productsData || [],
      users: usersData || [],
      nextReceiptNumber: ''
    });
    
    console.log('Dropdown data set successfully');
  } catch (error) {
    console.error('Error fetching dropdown data:', error);
    alert(`Error loading form data: ${error.message}`);
  } finally {
    setLoading(false);
  }
};
  
  const handleHeaderChange = (e) => {
  const { name, value } = e.target;
  
  if (name === 'productionOrderQty') {
    const newQty = parseFloat(value) || 0;
    
    // Update header with function form to ensure we have the latest state
    setHeader(prevHeader => {
      const newHeader = { ...prevHeader, [name]: value };
      
      // Update the first item's quantity when header quantity changes
      setItems(prevItems => {
        const newItems = [...prevItems];
        if (newItems.length > 0) {
          // For the first item (main product), recalculate calculated qty
          const mainProductQty = parseFloat(newItems[0].qty) || 0;
          newItems[0].calculatedQty = (newQty * mainProductQty).toString();
          newItems[0].value = calculateItemValue(newItems[0]);
        }
        
        // Recalculate byproducts
        for (let i = 1; i < newItems.length; i++) {
          const byproductQty = parseFloat(newItems[i].qty) || 0;
          newItems[i].calculatedQty = (newQty * byproductQty).toString();
          newItems[i].value = calculateItemValue(newItems[i]);
        }
        
        return newItems;
      });
      
      return newHeader;
    });
  } else {
    if (e.target.tagName === 'INPUT' && e.target.type === 'text') {
      setHeader(prevHeader => ({ ...prevHeader, [name]: toUpperCase(value) }));
    } else {
      setHeader(prevHeader => ({ ...prevHeader, [name]: value }));
    }
  }
};

 const handleItemChange = (idx, e) => {
  const { name, value } = e.target;
  
  setItems(prevItems => {
    const newItems = [...prevItems];
    
    if (name === 'qty') {
      const newQty = parseFloat(value) || 0;
      newItems[idx][name] = value;
      
      // Recalculate calculated qty: header qty * detail qty
      const headerQty = parseFloat(header.productionOrderQty) || 0;
      newItems[idx].calculatedQty = (headerQty * newQty).toString();
      newItems[idx].value = calculateItemValue(newItems[idx]);
      
      // If this is the first item (main product), also update the header qty
      if (idx === 0) {
        setHeader(prevHeader => ({
          ...prevHeader,
          productionOrderQty: value
        }));
      }
    } else {
      // Handle other field changes
      if (e.target.tagName === 'INPUT' && e.target.type === 'text') {
        newItems[idx][name] = toUpperCase(value);
      } else {
        newItems[idx][name] = value;
      }
      
      if (name === 'rate') {
        newItems[idx].value = calculateItemValue(newItems[idx]);
      }
    }
    
    return newItems;
  });
};
  
  // Handle production order selection - auto-fill details
const handleProductionOrderChange = async (productionOrderId) => {
  // Check if productionOrderId is undefined or null
  if (productionOrderId === undefined || productionOrderId === null) {
    console.error('Production order ID is undefined');
    return;
  }
  
  setHeader({ ...header, productionOrderId });
  
  if (productionOrderId) {
    try {
      // First, fetch all issue for production records to find the one for this production order
      console.log('Fetching issue for production records...');
      const issueResponse = await fetch(`${API_URL}/issue-for-production`);
      if (!issueResponse.ok) throw new Error('Failed to fetch issue for production records');
      const issueDataList = await issueResponse.json();
      
      console.log('Issue for production data list:', issueDataList);
      
      // Find the issue record for the selected production order
      const issueRecord = issueDataList.find(issue => issue.ProductionOrderID === productionOrderId);
      
      if (!issueRecord) {
        throw new Error(`No issue record found for production order ${productionOrderId}`);
      }
      
      console.log('Found issue record:', issueRecord);
      
      // Get the issued quantity from the issue record (for the receipt header)
      const issuedQty = parseFloat(issueRecord.ProductionOrderQty) || 0;
      
      // Now fetch the production order details to get the product and byproduct information
      const orderResponse = await fetch(`${API_URL}/production-order/${productionOrderId}`);
      if (!orderResponse.ok) throw new Error('Failed to fetch production order details');
      const orderData = await orderResponse.json();
      
      console.log('Production order data:', orderData);
      
      // Get the production order quantity from the production order header (for the first row)
      const productionOrderQty = parseFloat(orderData.Qty) || 0;
      
      // Set the original production order quantity (for recalculation purposes)
      setOriginalProductionOrderQty(issuedQty); // Use issued qty for recalculation
      
      // Prepare items array
      const newItems = [];
      
      // Add main product as first item with PRODUCTION ORDER quantity
      if (orderData) {
        // Find if this product allows batch and get tracking type
        const productInfo = dropdownData.products.find(p => p.ProductCode === orderData.ProductCode);
        const allowBatch = productInfo ? productInfo.AllowBatchOnProduct : false;
        // Parse AllowProductTracking as integer (0: None, 1: Batch, 2: Serial)
        const trackingType = parseInt(productInfo?.AllowProductTracking) || 0;
        
        // Calculate the quantity for the main product: header qty * detail qty
        const mainProductCalculatedQty = issuedQty * productionOrderQty;
        
        newItems.push({
          ...initialItem,
          materialType: 'Finished',
          productCode: orderData.ProductCode || '',
          productName: orderData.ProductName || '',
          qty: productionOrderQty.toString(), // Production order qty (detail qty)
          calculatedQty: mainProductCalculatedQty.toString(), // Header qty * detail qty
          uom: orderData.UOM || '',
          warehouse: '', // Will need to be set by user
          allowBatch: allowBatch,
          productTrackingType: trackingType,
          batches: [],
          serialNumbers: [],
          department_id: showDepartmentItemField ? "" : undefined,
        });
      }
      
      // Now fetch the production order details to get byproducts
      if (orderData.Details && orderData.Details.length > 0) {
        const byproductQtys = [];
        
        orderData.Details.forEach((detail) => {
          // Check if this detail is a byproduct (case-insensitive)
          if (detail.MaterialType && detail.MaterialType.toLowerCase() === 'byproduct') {
            const byproductQty = parseFloat(detail.Qty) || 0;
            byproductQtys.push(byproductQty);
            
            // Calculate the byproduct quantity: header qty * detail qty
            const byproductCalculatedQty = issuedQty * byproductQty;
            
            // Find if this product allows batch and get tracking type
            const productInfo = dropdownData.products.find(p => p.ProductCode === detail.ProductCode);
            const allowBatch = productInfo ? productInfo.AllowBatchOnProduct : false;
            // Parse AllowProductTracking as integer (0: None, 1: Batch, 2: Serial)
            const trackingType = parseInt(productInfo?.AllowProductTracking) || 0;
            
            newItems.push({
              ...initialItem,
              materialType: 'ByProduct',
              productCode: detail.ProductCode || '',
              productName: detail.ProductName || '',
              qty: byproductQty.toString(), // Byproduct qty (detail qty)
              calculatedQty: byproductCalculatedQty.toString(), // Header qty * detail qty
              uom: detail.UOM || '',
              flushType: detail.FlushType || '',
              warehouse: '', // Will need to be set by user
              allowBatch: allowBatch,
              productTrackingType: trackingType,
              batches: [],
              serialNumbers: [],
              department_id: showDepartmentItemField ? "" : undefined,
            });
          }
        });
        
        setOriginalByproductQtys(byproductQtys);
      }
      
      // If no items were added, add one empty item
      if (newItems.length === 0) {
        newItems.push({ ...initialItem });
      }
      
      // Set items first
      setItems(newItems);
      
      // Then update header with the ISSUED quantity (from issue table)
      setHeader(prev => ({
        ...prev,
        productionOrderQty: issuedQty.toString(), // This is the issued qty for the header
        productionOrderId: productionOrderId
      }));
      
    } catch (error) {
      console.error('Error fetching production order details:', error);
      alert('Error loading production order details: ' + error.message);
    }
  } else {
    // Clear items if no production order selected
    setItems([{ ...initialItem }]);
    setHeader(prev => ({
      ...prev,
      productionOrderQty: ''
    }));
    setOriginalProductionOrderQty(0);
    setOriginalByproductQtys([]);
  }
};
  
  // Handle batch and serial modal saves
  const handleSaveBatches = (batches) => {
    if (currentBatchItem !== null) {
      const newItems = [...items];
      // Format the batches to ensure expiry date is in the correct format
      const formattedBatches = batches.map(batch => ({
        ...batch,
        expiryDate: batch.expiryDate ? new Date(batch.expiryDate).toISOString().split('T')[0] : ""
      }));
      newItems[currentBatchItem] = {
        ...newItems[currentBatchItem],
        batches: formattedBatches
      };
      setItems(newItems);
      setShowBatchModal(false);
      setCurrentBatchItem(null);
    }
  };

  const handleSaveSerial = (serialNumbers) => {
    if (currentSerialItem !== null) {
      const newItems = [...items];
      newItems[currentSerialItem] = {
        ...newItems[currentSerialItem],
        serialNumbers
      };
      setItems(newItems);
      setShowSerialModal(false);
      setCurrentSerialItem(null);
    }
  };
  
 const addItem = async () => {
  // Get next detail number
  const nextDetailNumber = await fetchNextDetailNumber();
  
  setItems([...items, { 
    ...initialItem,
    receiptDetailID: nextDetailNumber,
    department_id: showDepartmentItemField ? "" : undefined,
  }]);
};
  
  const removeItem = (idx) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== idx));
    }
  };
  
  const validate = () => {
    const errs = {};
    if (!header.receiptId || !header.receiptId.trim()) errs.receiptId = 'Required';
    if (!header.receiptDate) errs.receiptDate = 'Required';
    if (!header.productionOrderId) errs.productionOrderId = 'Required';
    if (!header.receivedByUserId) errs.receivedByUserId = 'Required';
    if (!header.receivedByUserName) errs.receivedByUserName = 'Required';
    
    // Validate department field only if it's shown
    if (showDepartmentField && !header.department_id) errs.department_id = 'Required';
    
    items.forEach((item, idx) => {
      if (!item.productCode) errs[`productCode_${idx}`] = 'Required';
      if (!item.productName) errs[`productName_${idx}`] = 'Required';
      if (!item.qty) errs[`qty_${idx}`] = 'Required';
      if (!item.uom) errs[`uom_${idx}`] = 'Required';
      if (!item.warehouse) errs[`warehouse_${idx}`] = 'Required';
      
      // Validate tracking fields based on tracking type
      if (item.productTrackingType === 1) { // Batch
        if (!item.batches || item.batches.length === 0) {
          errs[`batches_${idx}`] = "At least one batch is required";
        } else {
          // Check each batch has required fields
          const batchErrors = [];
          item.batches.forEach((batch, batchIdx) => {
            if (!batch.batchNumber || batch.batchNumber.trim() === '') {
              batchErrors.push(`Batch ${batchIdx + 1}: Batch number is required`);
            }
            if (!batch.expiryDate) {
              batchErrors.push(`Batch ${batchIdx + 1}: Expiry date is required`);
            }
            if (!batch.quantity || parseFloat(batch.quantity) <= 0) {
              batchErrors.push(`Batch ${batchIdx + 1}: Quantity must be greater than 0`);
            }
          });
          
          if (batchErrors.length > 0) {
            errs[`batches_${idx}`] = batchErrors.join('; ');
          }
          
          // Check if total batch quantity equals accepted quantity
          const totalBatchQty = item.batches.reduce(
            (sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0
          );
          const acceptedQty = parseFloat(item.calculatedQty) || 0;
          
          if (Math.abs(totalBatchQty - acceptedQty) > 0.001) {
            errs[`batches_${idx}`] = (errs[`batches_${idx}`] ? errs[`batches_${idx}`] + '; ' : '') + 
              `Total batch quantity (${totalBatchQty}) must equal quantity (${acceptedQty})`;
          }
        }
      }
      
      if (item.productTrackingType === 2) { // Serial
        const expectedCount = parseInt(item.calculatedQty) || 0;
        if (!item.serialNumbers || item.serialNumbers.length !== expectedCount) {
          errs[`serialNumbers_${idx}`] = `All ${expectedCount} serial numbers are required`;
        }
      }
      
      // Validate department item field only if it's shown
      if (showDepartmentItemField && !item.department_id) errs[`department_id_${idx}`] = 'Required';
    });
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
 const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate() || isSubmitting) return;
  
  if (!currentUser || !currentUser.UserID) {
    alert('User information not available. Please refresh the page and log in again.');
    return;
  }
  
  setIsSubmitting(true);
  setLoading(true);
  
  try {
    // Get next transaction ID
    const nextTransactionId = await fetchNextTransactionId();
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Your session has expired. Please log in again.');
      window.location.href = '/login';
      return;
    }
    
    const statusValue = header.status === '1' || header.status === 'Active';
    
    const payload = {
      ReceiptID: header.receiptId,
      ReceiptDate: header.receiptDate,
      ProductionOrderID: header.productionOrderId,
      ProductionOrderQty: parseFloat(header.productionOrderQty) || 0,
      BranchCode: header.branchCode,
      ReceivedByUserID: parseInt(header.receivedByUserId),
      ReceivedByUserName: header.receivedByUserName,
      InventoryFlag: header.inventoryFlag === 'yes',
      Status: header.status, // Send status as string directly
      // Only include department_id if showDepartmentField is true
      ...(showDepartmentField && header.department_id ? { department_id: parseInt(header.department_id) } : {}),
      ...(isEdit ? { updated_by: parseInt(currentUser.UserID) } : { created_by: parseInt(currentUser.UserID) }),
      TransactionID: nextTransactionId, // Include the transaction ID
      Details: items.map(item => ({
        ReceiptDetailID: item.receiptDetailID || undefined,
        ProductionOrderDetailID: item.productionOrderDetailID || undefined,
        MaterialType: item.materialType,
        ProductCode: item.productCode,
        ProductName: item.productName,
        Qty: parseFloat(item.qty) || 0,
        CalculatedQty: parseFloat(item.calculatedQty) || 0, // Ensure calculated qty is sent
        Rate: parseFloat(item.rate) || 0,
        Value: parseFloat(item.value) || 0,
        FlushType: item.flushType,
        UOM: item.uom,
        BatchNumber: item.allowBatch ? item.batchNumber : null,
        ExpiryDate: item.allowBatch && item.expiryDate ? new Date(item.expiryDate) : null,
        Warehouse: item.warehouse,
        // Only include department_id if showDepartmentItemField is true
        ...(showDepartmentItemField && item.department_id ? { department_id: parseInt(item.department_id) } : {}),
        // Add tracking fields
        ProductTrackingType: item.productTrackingType,
        Batches: item.productTrackingType === 1 ? 
          (item.batches || []).map(batch => ({
            BatchNumber: (batch.batchNumber || batch.BatchNumber || "").toUpperCase(),
            ExpiryDate: batch.expiryDate || batch.ExpiryDate || "",
            Quantity: parseFloat(batch.quantity || batch.Quantity || 0)
          })) : 
          [],
        SerialNumbers: item.productTrackingType === 2 ? item.serialNumbers : [],
      }))
    };
    
    const url = isEdit 
      ? `${API_URL}/receipt-from-production/${header.receiptId}`
      : `${API_URL}/receipt-from-production`;
    
    const method = isEdit ? 'PUT' : 'POST';
    
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    
    if (response.status === 401) {
      localStorage.removeItem('authToken');
      alert('Your session has expired. Please log in again.');
      window.location.href = '/login';
      return;
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save receipt');
    }
    
    const result = await response.json();
    
    if (onSubmit) {
      onSubmit();
    }
    
    onClose();
  } catch (err) {
    console.error('Error saving receipt:', err);
    alert(err.message || 'Error saving receipt');
  } finally {
    setIsSubmitting(false);
    setLoading(false);
  }
};
  
  if (!open) return null;
  
  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          .uppercase-input {
            text-transform: uppercase;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      } ${
        isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'
      }`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Receipt from Production' : 'Create Receipt from Production'}</h2>
            <div className="flex items-center gap-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Loading overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3"></div>
                <p>Loading form data...</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Scrollable Content Area */}
        <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
          <form onSubmit={handleSubmit} className="space-y-6 pb-6">
            {/* Receipt Header */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">Receipt Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Receipt ID *</label>
                  <input 
                    name="receiptId" 
                    value={header.receiptId} 
                    onChange={handleHeaderChange} 
                    disabled={isEdit} // Only disabled for edit mode
                    className={`w-full border rounded-lg px-3 py-2 ${isEdit ? 'bg-[#F1F5F9]' : ''} border-[#E2E8F0] uppercase-input`}
                  />
                  {errors.receiptId && <span className="text-xs text-red-500">{errors.receiptId}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Receipt Date *</label>
                  <input 
                    type="date" 
                    name="receiptDate" 
                    value={header.receiptDate} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 ${errors.receiptDate ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  />
                  {errors.receiptDate && <span className="text-xs text-red-500">{errors.receiptDate}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Production Order ID *</label>
               <SearchableDropdown
  options={dropdownData.productionOrders || []}
  value={header.productionOrderId}
  onChange={handleProductionOrderChange}
  getOptionLabel={(order) => `${order.ProductionOrderID} - ${order.ProductionProductCode}`}
  getOptionValue={(order) => order.ProductionOrderID}
  getOptionSecondaryLabel={(order) => order.ProductionProductName}
  placeholder="Select Production Order"
  error={errors.productionOrderId}
  disabled={loading}
  className="uppercase"
/>
                  {errors.productionOrderId && <span className="text-xs text-red-500">{errors.productionOrderId}</span>}
                </div>
                <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Issued Quantity (from Issue Table)</label>
  <input 
    name="productionOrderQty" 
    type="number"
    min="0"
    value={header.productionOrderQty} 
    onChange={handleHeaderChange}
    className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
    readOnly
  />
  <p className="text-xs text-gray-500 mt-1">This is the quantity from the issue table</p>
</div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Received By User ID *</label>
                  <input 
                    name="receivedByUserId" 
                    value={header.receivedByUserId} 
                    onChange={handleHeaderChange}
                    readOnly
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                    title="Auto-populated from logged-in user"
                  />
                  {errors.receivedByUserId && <span className="text-xs text-red-500">{errors.receivedByUserId}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Received By User Name *</label>
                  <input 
                    name="receivedByUserName" 
                    value={header.receivedByUserName} 
                    onChange={handleHeaderChange}
                    readOnly
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                    title="Auto-populated from logged-in user"
                  />
                  {errors.receivedByUserName && <span className="text-xs text-red-500">{errors.receivedByUserName}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Branch</label>
                 <SearchableDropdown
  options={dropdownData.branches || []}
  value={header.branchCode}
  onChange={(branchCode) => {
    handleHeaderChange({
      target: { name: 'branchCode', value: branchCode }
    });
  }}
  getOptionLabel={(branch) => `${branch.BranchCode} - ${branch.BranchName}`}
  getOptionValue={(branch) => branch.BranchCode}
  getOptionSecondaryLabel={(branch) => branch.BranchName}
  placeholder="Select Branch"
  className="uppercase"
/>
                </div>
                
                {/* Department Selection Field - Only shown if ShowOnHeader is true */}
                {showDepartmentField && (
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Department *
                    </label>
                    <SearchableDropdown
  options={departments}
  value={header.department_id}
  onChange={(department_id) => {
    handleHeaderChange({
      target: { name: 'department_id', value: department_id }
    });
  }}
  getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
  getOptionValue={(dept) => dept.DepartmentID.toString()}
  getOptionSecondaryLabel={(dept) => dept.DepartmentName}
  placeholder={loadingDepartments ? 'Loading departments...' : 'Select Department'}
  error={errors.department_id}
  disabled={loadingDepartments}
  className="uppercase"
/>
                    {errors.department_id && (
                      <span className="text-xs text-red-500">
                        {errors.department_id}
                      </span>
                    )}
                  </div>
                )}
                
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Status</label>
                  <select 
                    name="status" 
                    value={header.status} 
                    onChange={handleHeaderChange} 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Add to Inventory *</label>
                  <select 
                    name="inventoryFlag" 
                    value={header.inventoryFlag} 
                    onChange={handleHeaderChange} 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                  >
                    {INVENTORY_FLAG_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Select "Yes" to add items to inventory, "No" to skip inventory update</p>
                </div>
              </div>
            </div>
            
            {/* Receipt Items */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#384959]">Receipt Items</h3>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors" 
                  onClick={addItem}
                >
                  <PlusIcon className="mr-1" /> Add Item
                </button>
              </div>
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full border border-[#E2E8F0]">
                  <thead>
                    <tr className="bg-[#BDDDFC] text-[#384959]">
                      <th className="p-2">#</th>
                      <th className="p-2">Material Type</th>
                      <th className="p-2">Product Code</th>
                      <th className="p-2">Product Name</th>
                      <th className="p-2">UOM</th>
                      <th className="p-2">Quantity</th>
                      <th className="p-2">Calculated Qty</th>
                      <th className="p-2">Rate</th>
                      <th className="p-2">Value</th>
                      <th className="p-2">Warehouse</th>
                      {/* Department column - Only shown if ShowOnItem is true */}
                      {showDepartmentItemField && <th className="p-2">Department</th>}
                      <th className="p-2">Tracking Type</th>
                      <th className="p-2">Batch/Serial Info</th>
                     
                      <th className="p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx} className="border-t border-[#E2E8F0]">
                        <td className="p-2 text-center">{idx + 1}</td>
                        <td className="p-2">
                          <select 
                            name="materialType" 
                            value={item.materialType} 
                            onChange={e => handleItemChange(idx, e)} 
                            className="w-24 border rounded-lg px-2 py-1 border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                          >
                            <option value="Finished">Finished</option>
                            <option value="ByProduct">ByProduct</option>
                          </select>
                        </td>
                        <td className="p-2">
                          <input 
                            name="productCode" 
                            value={item.productCode} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-32 border rounded-lg px-2 py-1 uppercase-input ${errors[`productCode_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`productCode_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="productName" 
                            value={item.productName} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-40 border rounded-lg px-2 py-1 uppercase-input ${errors[`productName_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`productName_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="uom" 
                            value={item.uom} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-16 border rounded-lg px-2 py-1 uppercase ${errors[`uom_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`uom_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="qty" 
                            type="number" 
                            min="0" 
                            value={item.qty} 
                            onChange={e => handleItemChange(idx, e)} 
                            readOnly={idx > 0} // Make byproduct qty read-only
                            className={`w-16 border rounded-lg px-2 py-1 ${errors[`qty_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] ${idx > 0 ? 'bg-gray-100' : ''}`}
                          />
                          {errors[`qty_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="calculatedQty" 
                            type="number" 
                            min="0" 
                            value={item.calculatedQty || ''} 
                            readOnly
                            className="w-16 border rounded-lg px-2 py-1 bg-gray-100 border-[#E2E8F0]"
                          />
                        </td>
                        <td className="p-2">
                          <input 
                            name="rate" 
                            type="number" 
                            min="0" 
                            step="0.01"
                            value={item.rate} 
                            onChange={e => handleItemChange(idx, e)} 
                            className="w-20 border rounded-lg px-2 py-1 border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                          />
                        </td>
                        <td className="p-2">
                          <input 
                            name="value" 
                            value={item.value} 
                            readOnly 
                            className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                          />
                        </td>
                        <td className="p-2">
                          <SearchableDropdown
  options={dropdownData.warehouses || []}
  value={item.warehouse}
  onChange={(warehouse) => {
    handleItemChange(idx, {
      target: { name: 'warehouse', value: warehouse }
    });
  }}
  getOptionLabel={(warehouse) => warehouse.LocationName}
  getOptionValue={(warehouse) => warehouse.LocationCode}
  getOptionSecondaryLabel={(warehouse) => warehouse.LocationCode}
  placeholder="Select Warehouse"
  width="w-24"
  error={errors[`warehouse_${idx}`]}
  className="uppercase"
/>
                          {errors[`warehouse_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        {/* Department cell - Only shown if ShowOnItem is true */}
                        {showDepartmentItemField && (
                          <td className="p-2">
                            <SearchableDropdown
  options={departments}
  value={item.department_id}
  onChange={(department_id) => {
    handleItemChange(idx, {
      target: { name: 'department_id', value: department_id }
    });
  }}
  getOptionLabel={(dept) => dept.DepartmentCode}
  getOptionValue={(dept) => dept.DepartmentID.toString()}
  getOptionSecondaryLabel={(dept) => dept.DepartmentName}
  placeholder="Select"
  width="w-24"
  error={errors[`department_id_${idx}`]}
  className="uppercase"
/>
                            {errors[`department_id_${idx}`] && (
                              <span className="text-xs text-red-500">*</span>
                            )}
                          </td>
                        )}
                        {/* Tracking Type Column */}
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.productTrackingType === 0 ? 'bg-gray-100 text-gray-800' :
                            item.productTrackingType === 1 ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {item.productTrackingType === 0 ? 'None' :
                             item.productTrackingType === 1 ? 'Batch' : 'Serial'}
                          </span>
                        </td>
                        {/* Batch/Serial Info Column */}
                        <td className="p-2">
                          {item.productTrackingType === 1 && (
                            <div>
                              <div className="text-xs font-medium">
                                {item.batches?.length || 0} batch{item.batches?.length !== 1 ? 'es' : ''}
                              </div>
                              <button
                                type="button"
                                className="text-blue-600 hover:text-blue-800 text-xs mt-1"
                                onClick={() => {
                                  setCurrentBatchItem(idx);
                                  setShowBatchModal(true);
                                }}
                              >
                                Edit Batches
                              </button>
                            </div>
                          )}
                          {item.productTrackingType === 2 && (
                            <div>
                              <div className="text-xs font-medium">
                                {item.serialNumbers?.length || 0} of {item.calculatedQty} serials
                              </div>
                              <button
                                type="button"
                                className="text-blue-600 hover:text-blue-800 text-xs mt-1"
                                onClick={() => {
                                  setCurrentSerialItem(idx);
                                  setShowSerialModal(true);
                                }}
                              >
                                Edit Serials
                              </button>
                            </div>
                          )}
                          {item.productTrackingType === 0 && (
                            <span className="text-gray-400 text-sm">No tracking</span>
                          )}
                        </td>
                        
                        
                        <td className="p-2 text-center">
                          <button 
                            type="button" 
                            className="text-red-500 hover:text-red-700 transition-colors" 
                            onClick={() => removeItem(idx)} 
                            disabled={items.length === 1}
                          >
                            <TrashIcon />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Total Summary Section */}
              <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">Total Quantity</span>
                    <span className="text-base font-semibold">{summary.totalQuantity}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">Total Value</span>
                    <span className="text-base font-semibold text-lg text-blue-600">{summary.totalValue}</span>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        
        {/* Fixed Footer with Buttons */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={loading || isSubmitting || !currentUser}
              onClick={handleSubmit}
            >
              {loading || isSubmitting ? 'Saving...' : (isEdit ? 'Update Receipt' : 'Save Receipt')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Tracking Modals */}
      <BatchTableModal
        isOpen={showBatchModal}
        onClose={() => {
          setShowBatchModal(false);
          setCurrentBatchItem(null);
        }}
        onSave={handleSaveBatches}
        initialBatches={currentBatchItem !== null ? items[currentBatchItem]?.batches || [] : []}
        productName={currentBatchItem !== null ? items[currentBatchItem]?.productName : ""}
        acceptedQuantity={currentBatchItem !== null ? parseFloat(items[currentBatchItem]?.calculatedQty) || 0 : 0}
      />

      <SerialModal
        isOpen={showSerialModal}
        onClose={() => {
          setShowSerialModal(false);
          setCurrentSerialItem(null);
        }}
        onSave={handleSaveSerial}
        count={currentSerialItem !== null ? parseInt(items[currentSerialItem]?.calculatedQty) || 0 : 0}
        initialSerialNumbers={currentSerialItem !== null ? items[currentSerialItem]?.serialNumbers : []}
        productName={currentSerialItem !== null ? items[currentSerialItem]?.productName : ""}
        locationCode={currentSerialItem !== null ? items[currentSerialItem]?.warehouse : ""}
      />
    </div>
  );
};

export default ReceiptFormModal;